/**
 * Security utilities for input sanitization, validation, and rate limiting
 */

// Input sanitization
export const sanitizeInput = (input: string): string => {
  if (!input || typeof input !== 'string') return '';
  
  return input
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers
    .replace(/data:/gi, '') // Remove data: protocol
    .trim()
    .slice(0, 1000); // Limit length
};

// Email validation with additional security checks
export const validateEmail = (email: string): boolean => {
  if (!email || typeof email !== 'string') return false;
  
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const isValid = emailRegex.test(email);
  
  // Additional security checks
  const hasValidLength = email.length >= 5 && email.length <= 254;
  const noConsecutiveDots = !email.includes('..');
  const noStartEndDots = !email.startsWith('.') && !email.endsWith('.');
  
  return isValid && hasValidLength && noConsecutiveDots && noStartEndDots;
};

// Password strength validation
export const validatePasswordStrength = (password: string): {
  isValid: boolean;
  score: number;
  feedback: string[];
} => {
  if (!password || typeof password !== 'string') {
    return { isValid: false, score: 0, feedback: ['Password is required'] };
  }

  const feedback: string[] = [];
  let score = 0;

  // Length check
  if (password.length >= 8) score += 2;
  else feedback.push('Password must be at least 8 characters long');

  if (password.length >= 12) score += 1;

  // Character variety checks
  if (/[a-z]/.test(password)) score += 1;
  else feedback.push('Include lowercase letters');

  if (/[A-Z]/.test(password)) score += 1;
  else feedback.push('Include uppercase letters');

  if (/\d/.test(password)) score += 1;
  else feedback.push('Include numbers');

  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score += 2;
  else feedback.push('Include special characters');

  // Common password checks
  const commonPasswords = ['password', '123456', 'qwerty', 'admin', 'letmein'];
  if (commonPasswords.some(common => password.toLowerCase().includes(common))) {
    score -= 3;
    feedback.push('Avoid common passwords');
  }

  return {
    isValid: score >= 5 && feedback.length === 0,
    score: Math.max(0, Math.min(10, score)),
    feedback
  };
};

// Environment variable validation
export const validateEnvironmentVariables = (): {
  isValid: boolean;
  missing: string[];
  invalid: string[];
} => {
  const required = [
    'VITE_SUPABASE_URL',
    'VITE_SUPABASE_ANON_KEY',
    'VITE_ELEVENLABS_API_KEY',
    'VITE_ELEVENLABS_AGENT_ID'
  ];

  const missing = required.filter(key => !import.meta.env[key] || import.meta.env[key] === `your_${key.toLowerCase()}_here`);
  const invalid: string[] = [];

  // Validate Supabase URL format
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (supabaseUrl && !supabaseUrl.includes('.supabase.co')) {
    invalid.push('VITE_SUPABASE_URL must be a valid Supabase project URL');
  }

  return {
    isValid: missing.length === 0 && invalid.length === 0,
    missing,
    invalid
  };
};

// URL validation
export const validateURL = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    // Only allow HTTPS in production
    const allowedProtocols = import.meta.env.PROD 
      ? ['https:'] 
      : ['http:', 'https:'];
    
    return allowedProtocols.includes(urlObj.protocol);
  } catch {
    return false;
  }
};